/**
 * SwiperSlider
 * @description Web component used to create sliders, configurable with data-options attribute.
 * @documentation https://swiperjs.com/swiper-api
 * @example
<swiper-slider>
  <div
    class="swiper"
    data-swiper
    data-options='{
      "threshold": 10,
      "loop": false,
      "scrollbar": true,
      "navigation": {
        "nextEl": ".swiper-button-next-{{ section.id }}",
        "prevEl": ".swiper-button-prev-{{ section.id }}"
      },
      "pagination": {
        "el": ".swiper-pagination-{{ section.id }}",
        "type": "bullets"
      },
      "slidesPerView": 1,
      "spaceBetween": 16,
      "breakpoints": {
        "768": {
          "slidesPerView": 2,
          "spaceBetween": 24
        },
        "1024": {
          "slidesPerView": 3,
          "spaceBetween": 32
        }
      }
    }'
  >
    <div class="swiper-wrapper">
      {%- for item in (1..8) -%}
        <div class="swiper-slide" data-swiper-slide-index="{{ forloop.index }}">
          <h2>
            Your slide
          </h2>
        </div>
      {%- endfor -%}
    </div>

    <button
      class="swiper-button-prev swiper-button-prev-{{ section.id }} "
      aria-label="{% render "format-translation", namespace: "accessibility", key: "previous_slide", fallback: "Previous slide" %}"
    >
      {%- render 'global-icon', icon: 'chevron-left', icon_class: 'w-5' -%}
    </button>
    <button
      class="swiper-button-next swiper-button-next-{{ section.id }} "
      aria-label="{% render "format-translation", namespace: "accessibility", key: "next_slide", fallback: "Next slide" %}"
    >
      {%- render 'global-icon', icon: 'chevron-right', icon_class: 'w-5' -%}
    </button>
      
    <div class="swiper-pagination swiper-pagination-{{ section.id }} flex justify-center"></div>
    <div class="swiper-scrollbar"></div>
  </div>
</swiper-slider>
 */

(async () => {
  if (!customElements.get('swiper-slider')) {
    const {
      default: Swiper,
      A11y,
      Autoplay,
      Navigation,
      Pagination,
      Scrollbar,
      EffectFade,
    } = await import('swiper');

    Swiper.use([A11y, Autoplay, Navigation, Pagination, Scrollbar, EffectFade]);

    class SwiperSlider extends HTMLElement {
      constructor() {
        super();
        this.swiper = this.querySelector('[data-swiper]');

        // Stop when the swiper is not found
        if (!this.swiper) {
          return;
        }

        // Default to improve user experience
        this.swiperOptions = {
          threshold: 10,
        };

        // Check if we have extra options on the HTML
        if (this.swiper.dataset.options) {
          const options = JSON.parse(this.swiper.dataset.options);
          if (options) {
            this.swiperOptions = {
              ...this.swiperOptions,
              ...options,
            };
          }
        }

        // Call swiper with selected swiper element and options
        this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);

        // Remove class given to the Swiper to prevent CLS - (Used on the Blog overview tags & Brand blocks section)
        if (this.swiper.classList.contains('js:opacity-0'))
          this.swiper.classList.remove('js:opacity-0');

        // Handle hotspot actions (used currently in the PDP)
        if (this.querySelector('[data-swiper-with-hotspots]')) {
          this.handleHotspots();
        }

        // Handle Info slider transitions (used currently in the PDP)
        if (this.querySelector('[data-swiper-info-slider]')) {
          this.handleInfoSliderTransition();
        }

        /**
         * Listen to extra events when in Shopify editor
         */
        if (Shopify.designMode) {
          // Update the swiper when the section event is triggered
          window.addEventListener('shopify:section:load', (event) => {
            this.swiperInstance.update();
            if (this.swiperOptions.navigation) {
              this.swiperInstance.navigation.init();
              this.swiperInstance.navigation.update();
            }
          });

          // When on block select go to the slide in front-end
          window.addEventListener('shopify:block:select', (event) => {
            this.swiperInstance.update();
            if (this.swiperOptions.navigation) {
              this.swiperInstance.navigation.init();
              this.swiperInstance.navigation.update();
            }
            this.handleBlockSelect(event);
          });

          // When on block deselect go to the slide in front-end
          window.addEventListener('shopify:block:deselect', (event) => {
            this.swiperInstance.update();
            if (this.swiperOptions.navigation) {
              this.swiperInstance.navigation.init();
              this.swiperInstance.navigation.update();
            }
            this.handleBlockSelect(event);
          });
        }
      }

      handleInfoSliderTransition() {
        this.swiperInstance.on('slideChangeTransitionStart', () => {
          // Image animation (rotation + scale)
          this.querySelectorAll('.slider__image').forEach((slideImage) => {
            slideImage.classList.remove('odd:-rotate-2');
          });
          const activeSlideImage = this.querySelector('.swiper-slide-active .slider__image');
          activeSlideImage.classList.add('odd:-rotate-2');
          activeSlideImage.style.scale = '1.1';

          // Pagination/Numbers vertical animation
          const paginationCustom = this.querySelector('[data-pagination-custom]');
          let currentTranslateY = paginationCustom.style.getPropertyValue('--tw-translate-y');
          currentTranslateY = currentTranslateY.replace('%', '');
          let newTranslateY;

          const totalNumberSlides = +paginationCustom.dataset.paginationCustom;
          const translatePerTransition = 100 / totalNumberSlides;

          // get the direction of the slide transition - there is nothing out of the box in the SwiperJS docs for it that gets the swipe and navigation transition
          let swiperNavigationDirection = 'previous';
          if (this.swiperInstance.activeIndex > this.swiperInstance.previousIndex)
            swiperNavigationDirection = 'next';

          if (swiperNavigationDirection == 'next') {
            if (this.swiperInstance.activeIndex > totalNumberSlides) {
              // translate it to the first slide when the current slide is the last slide
              newTranslateY = 0;
            } else {
              newTranslateY = +currentTranslateY - translatePerTransition;
            }
          } else {
            if (this.swiperInstance.activeIndex === 0) {
              // translate it to the last slide when the current slide is the 1st slide
              newTranslateY = -(translatePerTransition * (totalNumberSlides - 1));
            } else {
              newTranslateY = +currentTranslateY + translatePerTransition;
            }
          }

          paginationCustom.style.setProperty('--tw-translate-y', `${newTranslateY}%`);

          // set image to initial scale during transition progression
          setTimeout(() => {
            this.querySelector('.swiper-slide-active .slider__image').style.scale = '1';
          }, 180);
        });
      }

      /**
       * Handles the theme editor block change/edit event
       * @param {Object} event
       */
      handleBlockSelect(event) {
        // Check if the slide index is set
        if (!('swiperSlideIndex' in event.target.dataset)) {
          return;
        }

        // Set the slide index based on loop settings or not
        let swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) - 1;
        if (this.swiperOptions.loop) {
          swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) + 1;
        }

        // Slide to slide based on the data attribute from the target
        const sliderToUpdate = event.target.closest('[data-swiper]');
        sliderToUpdate.swiper.slideTo(swipeToSlideIndex, 1000);
      }

      /**
       * Handles the hotspots (used in the PDP atm)
       *
       */
      handleHotspots() {
        const swiperAndHotspotsWrapper = this.closest('[data-swiper-and-hotspots-wrapper]');
        const hotspots = swiperAndHotspotsWrapper.querySelectorAll('[data-hotspot]');

        // go to the correspondent slide of the hotspot clicked
        hotspots.forEach((hotspot) => {
          hotspot.addEventListener('click', () => {
            this.swiperInstance.slideTo(parseInt(hotspot.dataset.hotspot));
          });
        });

        // update the hotspot class according to the current slide
        this.swiperInstance.on('transitionEnd', () => {
          const activeHotspot = Array.from(hotspots).find(
            (currentHotspot) =>
              parseInt(currentHotspot.dataset.hotspot) === this.swiperInstance.activeIndex
          );
          hotspots.forEach((hotspot) => {
            hotspot.classList.remove('hotspot--active');
          });
          activeHotspot?.classList.add('hotspot--active');
        });
      }
    }

    window.SwiperSlider = SwiperSlider;

    customElements.define('swiper-slider', SwiperSlider);
  }
})();
